import React, { useState } from 'react';
import Modal, { ModalHeader } from '@xo-union/tk-component-modals';
import styles from './index.scss';
import { Subhead } from '@xo-union/tk-ui-typography';
import { Button } from '@xo-union/tk-component-buttons';
import { LegalDisclosure } from '../../LegalDisclosure';
import { IconButton } from '../../IconButton';
import { AiButton } from '../../AiButton/AiButton';
import AiThinking from '../../AiThinking';
import { AiLabel } from '../../AiLabel';
import { ModalErrorMessage } from './errorMessage';
import { AiSettingsPanel } from '../../AiSettingsPanel/AiSettingsPanel';
import { MoreButton } from './MoreButton/MoreButton';
import { track } from '../../../glm/utils/analytics';
import { compose } from '@xo-union/react-css-modules';
import { History } from './History/History';
import classNames from 'classnames';
import striptags from 'striptags';
import CopyToClipboardButton from '../../CopyToClipboardButton/CopyToClipboardButton';

enum TextGenerationModalState {
  Default,
  Settings,
  OnCloseConfirmation,
}

export interface TextGenerationModalProps {
  title?: string;
  isThinking: boolean;
  onSecondaryClick: () => void;
  onPrimaryClick: () => void;
  onClose: () => void;
  generatedText?: string;
  errorMessage?: string | null;
  retryRequest?: () => void;
  //add history prop to handle paginated responses
  isInZeroState?: boolean;
}

export const TextGenerationModal: React.FC<TextGenerationModalProps> = ({
  isThinking,
  title,
  generatedText = '',
  onSecondaryClick,
  onPrimaryClick,
  onClose,
  errorMessage,
  retryRequest,
  isInZeroState = false,
}: TextGenerationModalProps): JSX.Element => {
  const [modalState, setModalState] = useState<TextGenerationModalState>(TextGenerationModalState.Default);

  const areActionsDisabled = isThinking || !!errorMessage || isInZeroState;
  const shouldHideModalCloseButton = modalState === TextGenerationModalState.OnCloseConfirmation;

  const handleOnClose = () => {
    if (modalState === TextGenerationModalState.OnCloseConfirmation || isInZeroState) {
      onClose();
      return;
    }

    setModalState(TextGenerationModalState.OnCloseConfirmation);
  };

  const onCopy = () => {
    track('AI Button Clicked', {
      action: 'WWS AI',
      actionName: 'text copied',
    });
  };

  const handleOptionSelect = (optionValue: string) => {
    switch (optionValue) {
      case 'settings': {
        setModalState(TextGenerationModalState.Settings);
        break;
      }
      default:
        break;
    }
  };

  const renderModalContent = () => {
    return (
      <>
        <ModalHeader className={styles.modalHeader} onClose={onClose}>
          <AiLabel titleText="Quinn" />
        </ModalHeader>
        <div className={styles.textGenerationContentLinGradWrapper} data-testid="TextGenerationModal__content">
          <div className={styles.textGenerationContent}>
            <div className={styles.subHeadContainer}>
              <Subhead bold className={styles.title}>
                {title}
              </Subhead>
              {!isThinking && !errorMessage && !isInZeroState && <History className={styles.history} />}
            </div>
            {isThinking && <AiThinking className={styles.thinking} />}
            {!isThinking && errorMessage && <ModalErrorMessage onRetry={retryRequest} errorMessage={errorMessage} />}

            {!isThinking && !errorMessage && (
              <>
                <div className={styles.generatedTextContainer}>
                  {!!generatedText && (
                    <>
                      <div
                        data-testid="generated-text"
                        className={classNames(styles.generatedText, {
                          [styles.generatedText__subtle]: isInZeroState,
                        })}
                      >
                        {striptags(generatedText).replace(/&nbsp;/gi, ' ')}
                      </div>
                      {!isInZeroState && (
                        <CopyToClipboardButton
                          textToCopy={generatedText}
                          onCopy={onCopy}
                          onCopyMessage="Copied text to clipboard"
                          dataTestId="copy-button"
                          ariaLabel="Copy text"
                          className={styles.copyButton}
                        />
                      )}
                    </>
                  )}
                </div>
                <div className={styles.actionButtons}>
                  <div className={styles.buttonGroup}>
                    <AiButton
                      menuDirection="up"
                      isParentFocused
                      shouldRenderCustomPromptInput
                      defaultOpenElement={isInZeroState ? 'custom-prompt-input' : null}
                    />
                    <MoreButton onOptionSelect={handleOptionSelect} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className={styles.ctaButtons}>
          <Button disabled={areActionsDisabled} onClick={onSecondaryClick} size="md" color="secondary">
            Insert
          </Button>
          <Button disabled={areActionsDisabled} onClick={onPrimaryClick} size="md">
            Replace
          </Button>
        </div>
        <LegalDisclosure />
      </>
    );
  };

  const renderSettingsContent = () => {
    return (
      <>
        <span className={styles.TextGenerationModal__settings__header}>
          <IconButton
            name="back"
            arialabel="back to text generation"
            datatestid="back button"
            size="md"
            onClick={() => setModalState(TextGenerationModalState.Default)}
          />
          Settings
        </span>
        <AiSettingsPanel />
      </>
    );
  };

  const renderOnCloseConfirmationContent = () => {
    return (
      <div
        className={styles.TextGenerationModal__closeConfirmation}
        data-testid="TextGenerationModal__closeConfirmation"
      >
        <ModalHeader className={styles.modalHeader}>Are you sure?</ModalHeader>
        <div className={styles.TextGenerationModal__closeConfirmation__text}>
          {'You currently have unsaved changes.\n Would you still like to exit?'}
        </div>
        <div className={styles.TextGenerationModal__closeConfirmation__actionButtonContainer}>
          <Button
            onClick={() => setModalState(TextGenerationModalState.Default)}
            size="lg"
            color="secondary"
            className={styles.TextGenerationModal__closeConfirmation__actionButton}
            data-testid="TextGenerationModal__closeConfirmation__cancelButton"
          >
            No
          </Button>
          <Button
            onClick={handleOnClose}
            size="lg"
            className={styles.TextGenerationModal__closeConfirmation__actionButton}
            data-testid="TextGenerationModal__closeConfirmation__confirmButton"
          >
            Yes
          </Button>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (modalState) {
      case TextGenerationModalState.Settings:
        return renderSettingsContent();
      case TextGenerationModalState.OnCloseConfirmation:
        return renderOnCloseConfirmationContent();
      case TextGenerationModalState.Default:
      default:
        return renderModalContent();
    }
  };

  return (
    <>
      <Modal
        onClose={shouldHideModalCloseButton ? undefined : handleOnClose}
        size="lg"
        classes={compose({
          'close-button': shouldHideModalCloseButton ? styles.TextGenerationModal__hideCloseButton : undefined,
        })}
      >
        <div className={styles.TextGenerationModal__modalContent}>{renderContent()}</div>
      </Modal>
    </>
  );
};
